/**
 * Multi-platform ad tracking for public shop storefront.
 * Supports Facebook Pixel, Google Tag Manager (dataLayer), and TikTok Pixel.
 */

const CURRENCY = 'IDR';
const INIT_KEY = '__shopTrackingInitId';

function shopTrackingConfig(shop) {
  if (!shop) return null;
  const t = shop.tracking || shop;
  return {
    facebook: {
      enabled: !!(t.facebook_pixel_enabled && t.facebook_pixel_id),
      id: (t.facebook_pixel_id || '').trim(),
    },
    gtm: {
      enabled: !!(t.gtm_enabled && t.gtm_container_id),
      id: normalizeGTM(t.gtm_container_id),
    },
    tiktok: {
      enabled: !!(t.tiktok_pixel_enabled && t.tiktok_pixel_id),
      id: (t.tiktok_pixel_id || '').trim(),
    },
  };
}

function normalizeGTM(id) {
  if (!id) return '';
  let v = String(id).trim().toUpperCase();
  if (v && !v.startsWith('GTM-')) v = `GTM-${v.replace(/^GTM-?/, '')}`;
  return v;
}

function hasAnyTracking(cfg) {
  return cfg && (cfg.facebook.enabled || cfg.gtm.enabled || cfg.tiktok.enabled);
}

function ensureDataLayer() {
  window.dataLayer = window.dataLayer || [];
}

function pushDataLayer(event, payload = {}) {
  ensureDataLayer();
  window.dataLayer.push({ event, ...payload });
}

function injectInlineScript(id, code) {
  if (document.getElementById(id)) return;
  const s = document.createElement('script');
  s.id = id;
  s.text = code;
  document.head.appendChild(s);
}

function initFacebook(pixelId) {
  if (!pixelId || window.fbq) return;
  injectInlineScript('shop-fb-pixel-base', `
    !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
    n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
  `);
  window.fbq('init', pixelId);
  window.fbq('track', 'PageView');
}

function initGTM(containerId) {
  if (!containerId || document.getElementById('shop-gtm-script')) return;
  injectInlineScript('shop-gtm-script', `
    (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});
    var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';
    j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;
    j.id='shop-gtm-loader';f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer','${containerId}');
  `);
  if (!document.getElementById('shop-gtm-noscript')) {
    const noscript = document.createElement('noscript');
    noscript.id = 'shop-gtm-noscript';
    noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${containerId}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
    document.body.insertBefore(noscript, document.body.firstChild);
  }
  pushDataLayer('page_view', { page_location: window.location.href, page_path: window.location.pathname });
}

function initTikTok(pixelId) {
  if (!pixelId || window.ttq) return;
  injectInlineScript('shop-tiktok-pixel-base', `
    !function(w,d,t){w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];
    ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"];
    ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};
    for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);
    ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e};
    ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";
    ttq._i=ttq._i||{};ttq._i[e]=[];ttq._i[e]._u=i;ttq._t=ttq._t||{};ttq._t[e]=+new Date;ttq._o=ttq._o||{};ttq._o[e]=n||{};
    var o=document.createElement("script");o.type="text/javascript";o.async=!0;o.src=i+"?sdkid="+e+"&lib="+t;
    var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};
    }(window,document,'ttq');
  `);
  window.ttq.load(pixelId);
  window.ttq.page();
}

/** Initialize pixel scripts once per shop. Called from applyShopTheme(). */
export function initShopTracking(shop) {
  const cfg = shopTrackingConfig(shop);
  if (!hasAnyTracking(cfg)) return cfg;

  const shopKey = String(shop.id || shop.slug || 'unknown');
  if (window[INIT_KEY] === shopKey) return cfg;
  window[INIT_KEY] = shopKey;

  if (cfg.facebook.enabled) initFacebook(cfg.facebook.id);
  if (cfg.gtm.enabled) initGTM(cfg.gtm.id);
  if (cfg.tiktok.enabled) initTikTok(cfg.tiktok.id);

  return cfg;
}

export function isTrackingActive(shop) {
  return hasAnyTracking(shopTrackingConfig(shop));
}

function productPayload(item, qty = 1) {
  const id = String(item.product_id || item.id || item.course_id || '');
  const name = item.product_name || item.name || item.title || '';
  const price = Number(item.price || item.item_price || 0);
  const quantity = Number(qty || item.quantity || 1);
  const contentType = item.content_type || (item.type === 'course' || item.course_id ? 'product' : 'product');
  return {
    content_ids: [id],
    content_name: name,
    content_type: contentType,
    value: price * quantity,
    currency: CURRENCY,
    contents: [{ id, quantity, item_price: price }],
    num_items: quantity,
  };
}

function cartPayload(items) {
  const list = items || [];
  const contentIds = list.map(i => String(i.product_id || i.id));
  const value = list.reduce((s, i) => s + (Number(i.price) || 0) * (Number(i.quantity) || 1), 0);
  return {
    content_ids: contentIds,
    content_type: 'product',
    value,
    currency: CURRENCY,
    contents: list.map(i => ({
      id: String(i.product_id || i.id),
      quantity: Number(i.quantity) || 1,
      item_price: Number(i.price) || 0,
    })),
    num_items: list.reduce((s, i) => s + (Number(i.quantity) || 1), 0),
  };
}

export function trackPageView(extra = {}) {
  const payload = { page_location: window.location.href, page_path: window.location.pathname, ...extra };
  if (window.fbq) window.fbq('track', 'PageView');
  pushDataLayer('page_view', payload);
  if (window.ttq) window.ttq.page();
}

export function trackViewContent(item, qty = 1) {
  const p = productPayload(item, qty);
  if (window.fbq) window.fbq('track', 'ViewContent', p);
  pushDataLayer('view_content', { ecommerce: p });
  if (window.ttq) window.ttq.track('ViewContent', p);
}

export function trackViewCategory(categoryName, categoryId = '') {
  const p = {
    content_name: categoryName,
    content_category: categoryName,
    content_ids: categoryId ? [String(categoryId)] : [],
    content_type: 'product',
    currency: CURRENCY,
  };
  if (window.fbq) window.fbq('track', 'ViewCategory', p);
  pushDataLayer('view_category', { ecommerce: p });
  if (window.ttq) window.ttq.track('ViewCategory', p);
}

export function trackSearch(query) {
  const q = String(query || '').trim();
  if (!q) return;
  const p = { search_string: q, content_type: 'product' };
  if (window.fbq) window.fbq('track', 'Search', p);
  pushDataLayer('search', { search_term: q, ecommerce: p });
  if (window.ttq) window.ttq.track('Search', p);
}

export function trackAddToCart(item, qty = 1) {
  const p = productPayload(item, qty);
  if (window.fbq) window.fbq('track', 'AddToCart', p);
  pushDataLayer('add_to_cart', { ecommerce: p });
  if (window.ttq) window.ttq.track('AddToCart', p);
}

export function trackInitiateCheckout(items, totalValue = null) {
  const p = cartPayload(items);
  if (totalValue != null) p.value = Number(totalValue);
  if (window.fbq) window.fbq('track', 'InitiateCheckout', p);
  pushDataLayer('begin_checkout', { ecommerce: p });
  if (window.ttq) window.ttq.track('InitiateCheckout', p);
}

export function trackPurchase(order, items) {
  const orderId = String(order.order_id || order.invoice_number || order.id || '');
  const storageKey = `shop_tracked_purchase_${orderId}`;
  if (!orderId || sessionStorage.getItem(storageKey)) return;
  sessionStorage.setItem(storageKey, '1');

  const lineItems = (items || []).map(i => ({
    product_id: i.product_id || i.id,
    product_name: i.product_name || i.name,
    price: i.subtotal != null ? Math.round(Number(i.subtotal) / (Number(i.quantity) || 1)) : Number(i.price) || 0,
    quantity: Number(i.quantity) || 1,
  }));
  const p = {
    ...cartPayload(lineItems),
    value: Number(order.total_amount || order.total || 0),
    order_id: orderId,
  };
  if (window.fbq) window.fbq('track', 'Purchase', p);
  pushDataLayer('purchase', { ecommerce: { ...p, transaction_id: orderId } });
  if (window.ttq) window.ttq.track('CompletePayment', p);
}

/** Catalog feed compatible product object for Meta/TikTok catalogs. */
export function catalogProductObject(product, shopSlug) {
  return {
    id: String(product.id),
    title: product.name,
    description: (product.description || '').slice(0, 5000),
    availability: product.stock === 0 ? 'out of stock' : 'in stock',
    condition: 'new',
    price: `${Number(product.price || 0)} ${CURRENCY}`,
    link: `${window.location.origin}/toko/${shopSlug}/produk/${product.slug}`,
    image_link: product.image_url || '',
    brand: shopSlug,
    google_product_category: product.category_name || '',
  };
}

export { CURRENCY, shopTrackingConfig };
